import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from 'src/core/ui/PageHeader';
import Footer from 'src/core/ui/helpers/Footer';
import CustomButton from 'src/core/ui/CustomButton';
import ContentCardsContainer, { CardTitle, CardType, ContentCard } from 'src/core/ui/ContentCard';
import { CustomField } from 'src/core/forms/CustomField';
import { tableQuickSearch } from 'src/core/tables/generateTablePageButtons';
import menuItems from 'src/core/ui/menu/items';
import { pagesRoutes } from 'src/routes';

export default function OverviewPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();
    const routeIdx = useMemo(() => buildRouteIndex(pagesRoutes), []);
    const sections = useMemo(() => buildSectionsFromMenu(routeIdx), [routeIdx]);
    const [query, setQuery] = useState('');
    const visible = useMemo(() => filterSections(sections, query, key => String(t(key))), [sections, query, t]);
    const current = firstSegment(location.pathname);
    const [flash, setFlash] = useState(current);
    useEffect(() => setFlash(current), [current]);
    useEffect(() => {
        if (!current) return;
        let secondFrame: number;
        // Wait for the cards to paint before scrolling to the selected area.
        const firstFrame = requestAnimationFrame(() => { secondFrame = requestAnimationFrame(() => scrollToSection(current)); });
        return () => { cancelAnimationFrame(firstFrame); if (secondFrame !== undefined) cancelAnimationFrame(secondFrame); };
    }, [current]);
    const search = (value: string) => { if (value && !query) window.scrollTo({ top: 0 }); setQuery(value); };
    const openFirst = () => { const link = visible[0]?.groups[0]?.leaves[0]?.link; if (query && link) navigate(link); };
    return (
        <>
            <PageHeader {...{
                title: 'Overview', subTitle: 'All pages, grouped as in the menu',
                components: [
                    { position: 'start', component: <PageSearch query={query} onSearch={search} onOpen={openFirst} /> },
                ],
            }} />
            {/* Separate the area cards a little more, and briefly highlight the card of the area the user came from, e.g. Orders on /orders. */}
            <Box onAnimationEnd={() => setFlash('')} sx={{ '& > .MuiBox-root > .MuiGrid-container': { rowGap: 1.5 }, [`& .MuiGrid-item.${titleCase(flash || '-')} .MuiCard-root`]: { animation: 'overviewSectionFlash 2600ms ease-out 250ms' }, '@keyframes overviewSectionFlash': { '15%, 60%': { boxShadow: `0 0 0 3px ${theme.palette.primary.main}`, backgroundColor: alpha(theme.palette.primary.main, 0.06) } } }}>
                <ContentCardsContainer>
                    {visible.map(section => (
                        <SectionCard key={section.id} section={section} />
                    ))}
                    {!visible.length &&
                        <ContentCard cardId="NoMatchingPages" cardTypes={[CardType.noHeader]}>
                            <Typography>No pages match "{query}".</Typography>
                        </ContentCard>
                    }
                </ContentCardsContainer>
            </Box>
            <Footer />
        </>
    );
}

function SectionCard({ section }: { section: MenuSection }) {
    const { t } = useTranslation();
    return (
        <ContentCard cardId={titleCase(section.id)} cardHeader={<SectionTitle Icon={section.Icon} title={t(section.title)} />}>
            <Box id={section.id} sx={{ position: 'relative', top: -88 }} />
            <Grid item xs={12}>
                <Stack spacing={2.5}>
                    {section.groups.map((group, index) => (
                        <Box key={group.title ?? index}>
                            {group.title && <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: 15, fontWeight: 700 }}><CardTitle icon={group.Icon && <group.Icon />}>{t(group.title)}</CardTitle></Typography>}
                            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))' }}>
                                {group.leaves.map(leaf => (<PageTile key={leaf.link} leaf={leaf} />))}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Grid>
        </ContentCard>
    );
}

function PageSearch({ query, onSearch, onOpen }: { query: string; onSearch: (value: string) => void; onOpen: () => void }) {
    const ref = useRef<HTMLDivElement>(null);
    // Mouse/keyboard users can type straight away; touch devices keep the keyboard closed.
    useEffect(() => { if (window.matchMedia?.('(pointer: fine)')?.matches) ref.current?.querySelector('input')?.focus({ preventScroll: true }); }, []);
    return (
        <Box ref={ref} onKeyDown={event => { if (event.key === 'Enter') onOpen(); if (event.key === 'Escape') onSearch(''); }}>
            <CustomField stateProps={{ state: query, setState: onSearch }} options={{ ...tableQuickSearch.options, name: 'PageSearch', label: 'Find page', className: 'page-search' }} sx={tableQuickSearch.sx} />
        </Box>
    );
}

function SectionTitle({ Icon, title }: { Icon?: React.ElementType; title: string }) {
    const theme = useTheme();
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, fontSize: 22, lineHeight: 1.3 }}>
            {Icon && <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 1, color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1), '& svg': { fontSize: 24 } }}><Icon /></Box>}
            {title}
        </Box>
    );
}

function PageTile({ leaf }: { leaf: MenuLeaf }) {
    const { t } = useTranslation();
    const theme = useTheme();
    const { Icon } = leaf;
    const title = t(leaf.title);
    const newTitle = `New ${singular(leaf.title)}`;
    const preload = () => { leaf.preload?.(); leaf.createPreload?.(); };
    // CustomButton is the link; the wrapper turns it into a menu-like tile with a tinted icon and starts loading the page code on hover/focus.
    return (
        <Box onMouseEnter={preload} onFocus={preload} sx={{ display: 'flex', alignItems: 'center', minWidth: 0, minHeight: 56, pr: 0.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', transition: 'border-color .15s, background-color .15s', '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.04) }, '& .custom-button-view-page': { justifyContent: 'flex-start', alignSelf: 'stretch', mt: 0, px: 1.25, py: 1, color: 'text.primary', textAlign: 'left', lineHeight: 1.3, overflowWrap: 'anywhere', '&:hover': { bgcolor: 'transparent' }, '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 }, '& .MuiTouchRipple-root': { display: 'none' } }, '& .custom-button-view-page .MuiButton-startIcon': { flexShrink: 0, alignItems: 'center', justifyContent: 'center', width: 36, height: 36, ml: 0, mr: 1.5, borderRadius: 1, color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) } }}>
            <Box sx={{ display: 'flex', flex: '1 1 auto', minWidth: 0, alignSelf: 'stretch' }}>
                <CustomButton title={title} icon={Icon ? <Icon /> : <Typography component="span" aria-hidden="true" fontWeight={800}>{title[0]}</Typography>} actionUrl={leaf.link} variant="text" className="custom-button-view-page" />
            </Box>
            {leaf.createHref && <CustomButton iconButton title={newTitle} tooltipText={newTitle} icon={<AddIcon />} actionUrl={leaf.createHref} size="small" />}
        </Box>
    );
}

function buildRouteIndex(routes: RouteNode[]): RouteIndex {
    const pages = new Set<string>();
    const createByBase: Record<string, string> = {};
    const preloads: Record<string, Preload> = {};
    const walk = (nodes: RouteNode[], base: string) => {
        nodes.forEach(node => {
            const here = node.path ? join(base, node.path) : base;
            if (node.element) pages.add(here);
            const preload = React.isValidElement(node.element) ? (node.element.type as { preload?: Preload }).preload : undefined;
            if (preload) preloads[here] = preload;
            if (node.children?.length) walk(node.children, here);
            if (node.element && /\/create$/i.test(here)) createByBase[here.replace(/\/create$/i, '')] = here;
        });
    };
    walk(routes, '');
    return { pages, createByBase, preloads };
}

function collectLeaves(node: MenuItemNode, routeIdx: RouteIndex): MenuLeaf[] {
    if (node.items?.length) return node.items.flatMap(item => collectLeaves(item, routeIdx));
    const link = node.link ? normalize(node.link) : '';
    if (!routeIdx.pages.has(link) || isAction(link)) return [];
    const createHref = routeIdx.createByBase[link];
    return [{ title: node.name, link, Icon: node.icon, createHref, preload: routeIdx.preloads[link], createPreload: createHref ? routeIdx.preloads[createHref] : undefined }];
}

// Direct menu leaves share an untitled group; a nested menu item becomes a titled group, as in the sidebar.
function buildSectionsFromMenu(routeIdx: RouteIndex): MenuSection[] {
    const sections: Record<string, MenuSection> = {};
    (menuItems as MenuHeading[]).forEach(heading => {
        heading.items.forEach(root => {
            if (!root.items?.length) return;
            const groups: MenuGroup[] = [];
            root.items.forEach(item => {
                const leaves = collectLeaves(item, routeIdx);
                if (!leaves.length) return;
                const last = groups[groups.length - 1];
                if (item.items?.length) groups.push({ title: item.name, Icon: item.icon, leaves });
                else if (last && !last.title) last.leaves.push(...leaves);
                else groups.push({ leaves });
            });
            if (!groups.length) return;
            const id = firstSegment(groups[0].leaves[0].link);
            if (!sections[id]) sections[id] = { id, title: root.name, Icon: root.icon, groups: [] };
            sections[id].groups.push(...groups);
        });
    });
    return Object.values(sections);
}

// A search term matches a page by its own, its subheading's or its area's name, in English or the current language.
function filterSections(sections: MenuSection[], query: string, translate: (key: string) => string): MenuSection[] {
    const needle = query.trim().toLowerCase();
    if (!needle) return sections;
    const matches = (...names: (string | undefined)[]) => names.some(name => name && (name.toLowerCase().includes(needle) || translate(name).toLowerCase().includes(needle)));
    return sections.map(section => ({ ...section, groups: section.groups.map(group => ({ ...group, leaves: group.leaves.filter(leaf => matches(section.title, group.title, leaf.title)) })).filter(group => group.leaves.length) })).filter(section => section.groups.length);
}

const normalize = (path: string) => ('/' + path).replace(/\/+/g, '/').replace(/\/$/, '') || '/';
const join = (base: string, path: string) => normalize(`${base}/${path}`);
const firstSegment = (path: string) => normalize(path).split('/')[1] || '';
const isAction = (link: string) => /\/logout$/i.test(link);
const singular = (title: string) => title.replace(/ies$/i, 'y').replace(/([^s])s$/i, '$1');
const titleCase = (input: string) => input.toLowerCase().replace(/(^|[\s\-_/])(\w)/g, (_, delimiter, letter) => (delimiter || '') + letter.toUpperCase()).replace(/[-_/]/g, ' ').replace(/\s+/g, ' ').trim();

function scrollToSection(id: string, offset = 88) {
    const element = document.getElementById(id);
    if (!element) return;
    window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
}

type RouteNode = { path?: string; element?: React.ReactNode; children?: RouteNode[] };
type Preload = () => Promise<unknown>;
type RouteIndex = { pages: Set<string>; createByBase: Record<string, string>; preloads: Record<string, Preload> };
type MenuLeaf = { title: string; link: string; Icon?: React.ElementType; createHref?: string; preload?: Preload; createPreload?: Preload };
type MenuGroup = { title?: string; Icon?: React.ElementType; leaves: MenuLeaf[] };
type MenuSection = { id: string; title: string; Icon?: React.ElementType; groups: MenuGroup[] };
type MenuItemNode = { name: string; link?: string; icon?: React.ElementType; items?: MenuItemNode[] };
type MenuHeading = { items: MenuItemNode[] };
